/**
 * Pro 2 intro: the loader screen from viture.com/pro2 (its first section, "s01").
 * A warm wash fades off the portrait while the glasses outline draws and the counter runs to 100,
 * then the portrait fades into a rippling WebGL background and the page scrolls on to the next section.
 * Shader, palettes, layout maths and timings are taken from the source page.
 */
if (!customElements.get('pro2-intro')) {
  const MOBILE_QUERY = '(max-width: 640px)';
  // Source photo is 2560×1443; the glasses outline is placed over its glasses on the 1920×1080 stage
  const PHOTO = { w: 2560, h: 1443 };
  const GLASSES = { left: 688, top: 329, w: 522, h: 167, viewW: 506, viewH: 158 };

  // Four-stop palettes [deep, mid, light, accent]
  const PALETTES = {
    intro: ['#04060d', '#0c1c46', '#16307c', '#2a1160'],
    next: ['#04060d', '#0b1533', '#122b66', '#2a0b5c'],
    warm: ['#ffe9cf', '#ff9a55', '#ff692c', '#a63a12'],
  };

  const VERTEX = `#version 300 es
void main() {
  // fullscreen triangle from gl_VertexID, no buffers needed
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

  const FRAGMENT = `#version 300 es
//__DEFINES__
precision highp float;
out vec4 outColor;

uniform vec2 uRes;
uniform float uTime;
uniform float uGrain;
uniform vec3 uC0;
uniform vec3 uC1;
uniform vec3 uC2;
uniform vec3 uC3;

// 2D simplex noise (Ashima Arts / Ian McEwan, MIT)
vec3 permute(vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                      -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.62;
  for (int i = 0; i < 3; i++) {
    v += a * snoise(p);
    p = p * 1.9 + vec2(7.3, 3.1);
    a *= 0.38;
  }
  return v;
}

void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uRes) / min(uRes.x, uRes.y);
  float t = uTime;

  // two-pass domain warp: slow, low-frequency marbling
  vec2 q = vec2(
    fbm(p * 0.62 + vec2(0.0, 0.05 * t)),
    fbm(p * 0.62 + vec2(5.2, 1.3) - vec2(0.04 * t, 0.018 * t))
  );
  vec2 r = vec2(
    fbm(p * 0.62 + 1.1 * q + vec2(1.7, 9.2) + vec2(0.07 * t, 0.026 * t)),
    fbm(p * 0.62 + 1.1 * q + vec2(8.3, 2.8) - vec2(0.024 * t, 0.055 * t))
  );
  float f = fbm(p * 0.62 + 1.25 * r);

#ifdef DETAIL
  float d2 = fbm(p * 2.35 + 2.1 * r + vec2(0.021 * t, -0.016 * t));
  f += 0.22 * d2;
#endif

  float n = clamp(f * 0.5 + 0.5, 0.0, 1.0);

  vec3 col = mix(uC0, uC1, smoothstep(0.0, 0.85, n));
  col = mix(col, uC2, smoothstep(0.2, 1.0, 0.5 + 0.5 * q.x));
  col = mix(col, uC3, smoothstep(0.45, 1.05, 0.5 + 0.5 * r.y) * 0.8);

#ifdef DETAIL
  float hd = 0.5 + 0.5 * snoise(p * 1.15 + 0.8 * r + vec2(0.013 * t, 0.009 * t));
  col = mix(col, mix(uC1, uC2, hd), 0.10);
  col = mix(col, uC3, 0.06 * smoothstep(0.25, 0.9, 0.5 + 0.5 * d2));
#endif

  // static film grain to break up banding
  float seed = dot(gl_FragCoord.xy, vec2(12.9898, 78.233));
  col += (fract(sin(seed) * 43758.5453) - 0.5) * uGrain;

  outColor = vec4(col, 1.0);
}`;

  /** @typedef {[number, number, number]} Rgb Red, green and blue channels, each 0–1 */
  /** @typedef {(progress: number) => number} Easing */

  /**
   * @typedef {object} FluidOptions
   * @property {() => Rgb[]} getColors The four palette stops to draw this frame
   * @property {number} [speed]
   * @property {number} [grain]
   */

  /**
   * @typedef {object} FluidUniforms
   * @property {WebGLUniformLocation | null} res
   * @property {WebGLUniformLocation | null} time
   * @property {WebGLUniformLocation | null} grain
   * @property {Array<WebGLUniformLocation | null>} colors
   */

  /**
   * @typedef {object} Placement Position and size of the glasses outline, in stage pixels
   * @property {number} left
   * @property {number} top
   * @property {number} w
   * @property {number} h
   */

  /** @typedef {[start: number, duration: number, easing: Easing, apply: (progress: number) => void]} Tween */
  /** @typedef {[at: number, callback: () => void]} TimedCall */

  /**
   * @param {string} hex
   * @returns {Rgb}
   */
  const hexToRgb = (hex) => {
    const packed = parseInt(hex.slice(1), 16);
    return [((packed >> 16) & 255) / 255, ((packed >> 8) & 255) / 255, (packed & 255) / 255];
  };

  /**
   * @param {number} value
   * @returns {number}
   */
  const clamp01 = (value) => Math.min(1, Math.max(0, value));
  /** @type {Record<'linear' | 'power1InOut' | 'power2InOut' | 'power2Out' | 'sineOut' | 'sineInOut' | 'expoInOut', Easing>} */
  const ease = {
    linear: (progress) => progress,
    power1InOut: (progress) => (progress < 0.5 ? 2 * progress * progress : 1 - (-2 * progress + 2) ** 2 / 2),
    power2InOut: (progress) => (progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2),
    power2Out: (progress) => 1 - (1 - progress) ** 3,
    sineOut: (progress) => Math.sin((progress * Math.PI) / 2),
    sineInOut: (progress) => -(Math.cos(Math.PI * progress) - 1) / 2,
    expoInOut: (progress) =>
      progress === 0
        ? 0
        : progress === 1
          ? 1
          : progress < 0.5
            ? 2 ** (20 * progress - 10) / 2
            : (2 - 2 ** (-20 * progress + 10)) / 2,
  };

  /**
   * Cover-fit of the 2560×1443 photo (inset -2px) into a width×height box: scale and offset
   * @param {number} width
   * @param {number} height
   */
  const coverPhoto = (width, height) => {
    const boxWidth = width + 4;
    const boxHeight = height + 4;
    const scale = Math.max(boxWidth / PHOTO.w, boxHeight / PHOTO.h);
    return { k: scale, x: -2 + (boxWidth - PHOTO.w * scale) / 2, y: -2 + (boxHeight - PHOTO.h * scale) / 2 };
  };

  /** Animated marble field on a canvas; `getColors` picks the palette each frame. */
  class Fluid {
    /**
     * @param {HTMLCanvasElement} canvas
     * @param {FluidOptions} options
     */
    constructor(canvas, { getColors, speed = 1, grain = 0.028 }) {
      this.canvas = canvas;
      this.getColors = getColors;
      this.grain = grain;
      this.speed = speed * (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0.15 : 1);
      this.time = 0;
      this.running = false;
      /** @type {WebGL2RenderingContext | null} */
      this.gl = canvas.getContext('webgl2', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        powerPreference: 'low-power',
      });
      if (!this.gl) return;

      try {
        this.#setup();
      } catch (error) {
        console.warn('[pro2-intro]', error);
        this.gl = null;
        return;
      }
      this.onResize = () => this.#resize();
      this.onVisibility = () => (document.hidden ? this.#halt() : this.running && this.#loop());
      /** @param {Event} event */
      this.onLost = (event) => {
        event.preventDefault();
        this.#halt();
      };
      this.onRestored = () => {
        this.#setup();
        if (this.running) this.#loop();
      };
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibility);
      canvas.addEventListener('webglcontextlost', this.onLost);
      canvas.addEventListener('webglcontextrestored', this.onRestored);
    }

    #setup() {
      const gl = /** @type {WebGL2RenderingContext} */ (this.gl);
      const isSoftwareRenderer = (() => {
        try {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          const rendererName = String(
            gl.getParameter(debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? ''
          );
          return /swiftshader|llvmpipe|softpipe|software|basic render/i.test(rendererName);
        } catch {
          return false;
        }
      })();
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      // Render below device resolution: the field is soft, and it keeps weak GPUs smooth
      /** @type {number} */
      this.resScale = isSafari ? 0.5 : isSoftwareRenderer ? 0.66 : 1;
      /** @type {number} */
      this.maxDpr = isSafari ? 1 : 1.5;

      /**
       * @param {number} type gl.VERTEX_SHADER or gl.FRAGMENT_SHADER
       * @param {string} source
       * @returns {WebGLShader}
       */
      const compileShader = (type, source) => {
        const shader = /** @type {WebGLShader} */ (gl.createShader(type));
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
          const log = gl.getShaderInfoLog(shader);
          gl.deleteShader(shader);
          throw new Error(`fluid shader compile failed: ${log}`);
        }
        return shader;
      };
      const program = /** @type {WebGLProgram} */ (gl.createProgram());
      gl.attachShader(program, compileShader(gl.VERTEX_SHADER, VERTEX));
      gl.attachShader(
        program,
        compileShader(gl.FRAGMENT_SHADER, FRAGMENT.replace('//__DEFINES__', isSoftwareRenderer ? '' : '#define DETAIL 1'))
      );
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(`fluid program link failed: ${gl.getProgramInfoLog(program)}`);
      }
      gl.useProgram(program);
      /** @type {FluidUniforms} */
      this.uniforms = {
        res: gl.getUniformLocation(program, 'uRes'),
        time: gl.getUniformLocation(program, 'uTime'),
        grain: gl.getUniformLocation(program, 'uGrain'),
        colors: [0, 1, 2, 3].map((index) => gl.getUniformLocation(program, `uC${index}`)),
      };
      this.#resize(true);
    }

    #resize(force = false) {
      const gl = this.gl;
      if (!gl || gl.isContextLost()) return;
      const rect = this.canvas.getBoundingClientRect();
      const width = rect.width >= 2 ? rect.width : window.innerWidth;
      const height = rect.height >= 2 ? rect.height : window.innerHeight;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, /** @type {number} */ (this.maxDpr)) * /** @type {number} */ (this.resScale);
      const canvasWidth = Math.max(2, Math.round(width * pixelRatio));
      const canvasHeight = Math.max(2, Math.round(height * pixelRatio));
      if (force || this.canvas.width !== canvasWidth || this.canvas.height !== canvasHeight) {
        this.canvas.width = canvasWidth;
        this.canvas.height = canvasHeight;
        gl.viewport(0, 0, canvasWidth, canvasHeight);
      }
    }

    start() {
      this.running = true;
      if (!document.hidden) this.#loop();
    }

    stop() {
      this.running = false;
      this.#halt();
    }

    #loop() {
      if (!this.gl || this.frame) return;
      /** @type {number} */
      this.last = performance.now();
      /** @param {number} now */
      const draw = (now) => {
        /** @type {number} */
        this.frame = requestAnimationFrame(draw);
        const gl = /** @type {WebGL2RenderingContext} */ (this.gl);
        if (gl.isContextLost()) return;
        this.time += 0.001 * Math.min(100, now - /** @type {number} */ (this.last)) * this.speed;
        this.last = now;
        gl.uniform2f(/** @type {FluidUniforms} */ (this.uniforms).res, this.canvas.width, this.canvas.height);
        gl.uniform1f(/** @type {FluidUniforms} */ (this.uniforms).time, this.time);
        gl.uniform1f(/** @type {FluidUniforms} */ (this.uniforms).grain, this.grain);
        this.getColors().forEach((color, index) =>
          gl.uniform3f(/** @type {WebGLUniformLocation | null} */ (/** @type {FluidUniforms} */ (this.uniforms).colors[index]), color[0], color[1], color[2])
        );
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };
      this.frame = requestAnimationFrame(draw);
    }

    #halt() {
      cancelAnimationFrame(/** @type {number} */ (this.frame));
      this.frame = 0;
    }

    destroy() {
      this.stop();
      if (!this.gl) return;
      window.removeEventListener('resize', /** @type {EventListener} */ (this.onResize));
      document.removeEventListener('visibilitychange', /** @type {EventListener} */ (this.onVisibility));
      this.canvas.removeEventListener('webglcontextlost', /** @type {EventListener} */ (this.onLost));
      this.canvas.removeEventListener('webglcontextrestored', /** @type {EventListener} */ (this.onRestored));
      this.gl.getExtension('WEBGL_lose_context')?.loseContext();
      this.gl = null;
    }
  }

  class Pro2Intro extends HTMLElement {
    connectedCallback() {
      // Always start on the intro: without this, a reload jumps back to the old scroll position
      // partway through the intro once the page finishes loading
      if (this.#autoScrollEnabled() && !location.hash) {
        history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
      }

      /** @type {MediaQueryList} */
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      const currentStage = () =>
        /** @type {HTMLElement} */ (this.querySelector(`.pro2-intro__stage--${/** @type {MediaQueryList} */ (this.mobileQuery).matches ? 'm' : 'd'}`));
      /** @type {HTMLElement[]} */
      this.counts = Array.from(this.querySelectorAll('[data-count]'));
      /** @type {HTMLElement[]} */
      this.loaders = Array.from(this.querySelectorAll('.pro2-intro__loader'));
      /** @type {HTMLElement[]} */
      this.veils = Array.from(this.querySelectorAll('.pro2-intro__veil'));
      /** @type {HTMLElement[]} */
      this.veilFluids = Array.from(this.querySelectorAll('.pro2-intro__veil-fluid'));
      /** @type {HTMLElement[]} */
      this.glows = Array.from(this.querySelectorAll('.pro2-intro__glowfx'));
      /** @type {HTMLElement[]} */
      this.photos = Array.from(this.querySelectorAll('.pro2-intro__photo'));
      /** @type {HTMLElement[]} */
      this.scalers = Array.from(this.querySelectorAll('.pro2-intro__gscale'));
      /** @type {SVGElement[]} */
      this.outers = Array.from(this.querySelectorAll('.pro2-intro__draw--outer'));
      /** @type {SVGElement[]} */
      this.bridges = Array.from(this.querySelectorAll('.pro2-intro__draw--bridge'));
      /** @type {HTMLElement} */
      this.feather = /** @type {HTMLElement} */ (this.querySelector('.pro2-intro__feather'));

      /** @type {() => void} */
      this.onResize = () => {
        cancelAnimationFrame(/** @type {number} */ (this.resizeFrame));
        /** @type {number} */
        this.resizeFrame = requestAnimationFrame(() => this.#layout());
      };
      this.#layout();
      window.addEventListener('resize', this.onResize);
      this.mobileQuery.addEventListener('change', this.onResize);

      // Background field: intro palette, blending to the next section's as the intro scrolls away
      const introColors = PALETTES.intro.map(hexToRgb);
      const nextColors = PALETTES.next.map(hexToRgb);
      const mixedColors = introColors.map((color) => /** @type {Rgb} */ ([...color]));
      /** @type {Fluid} */
      this.fluid = new Fluid(/** @type {HTMLCanvasElement} */ (this.querySelector('.pro2-intro__fluid')), {
        getColors: () => {
          const rect = this.getBoundingClientRect();
          const progress = clamp01(-rect.top / Math.max(1, rect.height));
          const blend = progress * progress * (3 - 2 * progress);
          // @ts-expect-error noUncheckedIndexedAccess: stop and channel stay inside the fixed 4-stop × 3-channel palettes
          for (let stop = 0; stop < 4; stop++) for (let channel = 0; channel < 3; channel++) mixedColors[stop][channel] = introColors[stop][channel] + (nextColors[stop][channel] - introColors[stop][channel]) * blend;
          return mixedColors;
        },
      });
      // Only render while on screen
      /** @type {IntersectionObserver} */
      this.visibility = new IntersectionObserver(([entry]) =>
        /** @type {IntersectionObserverEntry} */ (entry).isIntersecting ? /** @type {Fluid} */ (this.fluid).start() : /** @type {Fluid} */ (this.fluid).stop()
      );
      this.visibility.observe(this);

      const warmColors = PALETTES.warm.map(hexToRgb);
      /** @type {Fluid} */
      this.veilFluid = new Fluid(
        /** @type {HTMLCanvasElement} */ (currentStage().querySelector('.pro2-intro__veil-fluid')),
        {
          getColors: () => warmColors,
          speed: 1.6,
          grain: 0.03,
        }
      );
      this.veilFluid.start();

      this.#play();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', /** @type {EventListener} */ (this.onResize));
      /** @type {MediaQueryList} */ (this.mobileQuery).removeEventListener('change', /** @type {EventListener} */ (this.onResize));
      this.visibility?.disconnect();
      cancelAnimationFrame(/** @type {number} */ (this.resizeFrame));
      cancelAnimationFrame(/** @type {number} */ (this.timelineFrame));
      this.fluid?.destroy();
      this.veilFluid?.destroy();
      this.#cancelAutoScroll();
      this.#unlock();
      window.removeEventListener('scroll', /** @type {EventListener} */ (this.onScroll));
      this.interactEvents?.forEach((type) => window.removeEventListener(type, /** @type {EventListener} */ (this.onInteract)));
    }

    // Size the stages to cover the section, then pin the outline over the photo's glasses
    #layout() {
      const width = this.clientWidth;
      const height = this.clientHeight;
      let mobileStageHeight = 812;
      if (/** @type {MediaQueryList} */ (this.mobileQuery).matches && width > 0) {
        // Mobile stage is 375 wide and as tall as the viewport at that width
        mobileStageHeight = (height * 375) / width;
        this.style.setProperty('--stage-h', String(mobileStageHeight));
        this.style.setProperty('--scale', String(width / 375));
      } else {
        this.style.removeProperty('--stage-h');
        this.style.setProperty('--scale', String(Math.max(width / 1920, height / 1080)));
      }

      const svg = /** @type {SVGElement} */ (this.querySelector('.pro2-intro__stage--d .pro2-intro__glasses'));
      const box = this.#outlineBox(svg);
      const scaleX = GLASSES.w / box.w;
      const scaleY = GLASSES.h / box.h;
      /** @type {Placement} */
      const desktop = {
        left: GLASSES.left - box.x * scaleX,
        top: GLASSES.top - box.y * scaleY,
        w: GLASSES.viewW * scaleX,
        h: GLASSES.viewH * scaleY,
      };
      // Same spot on the photo, mapped through the mobile stage's cover-fit
      const desktopFit = coverPhoto(1920, 1080);
      const mobileFit = coverPhoto(375, mobileStageHeight);
      const fitScale = mobileFit.k / desktopFit.k;
      /** @type {Placement} */
      const mobile = {
        left: mobileFit.x + (desktop.left - desktopFit.x) * fitScale,
        top: mobileFit.y + (desktop.top - desktopFit.y) * fitScale,
        w: desktop.w * fitScale,
        h: desktop.h * fitScale,
      };
      for (const [glasses, placement] of /** @type {Array<[SVGElement, Placement]>} */ ([
        [svg, desktop],
        [this.querySelector('.pro2-intro__stage--m .pro2-intro__glasses'), mobile],
      ])) {
        glasses.style.left = `${placement.left.toFixed(2)}px`;
        glasses.style.top = `${placement.top.toFixed(2)}px`;
        glasses.style.width = `${placement.w.toFixed(2)}px`;
        glasses.style.height = `${placement.h.toFixed(2)}px`;
      }
    }

    /**
     * Bounding box of the drawn strokes in viewBox units
     * @param {SVGElement} svg
     * @returns {{ x: number, y: number, w: number, h: number }}
     */
    #outlineBox(svg) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      try {
        for (const path of /** @type {NodeListOf<SVGGraphicsElement>} */ (svg.querySelectorAll('.pro2-intro__draw'))) {
          const bounds = path.getBBox();
          const halfStroke = (parseFloat(getComputedStyle(path).strokeWidth) || 1.5) / 2;
          minX = Math.min(minX, bounds.x - halfStroke);
          minY = Math.min(minY, bounds.y - halfStroke);
          maxX = Math.max(maxX, bounds.x + bounds.width + halfStroke);
          maxY = Math.max(maxY, bounds.y + bounds.height + halfStroke);
        }
      } catch {
        // getBBox throws while hidden in some browsers
      }
      if (!Number.isFinite(minX) || maxX - minX < GLASSES.viewW / 2) return { x: 0, y: 0, w: GLASSES.viewW, h: GLASSES.viewH };
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }

    // The source's intro timeline, in seconds from start
    #play() {
      /**
       * @template {Element} T
       * @param {T[]} elements
       * @param {(element: T) => void} update
       */
      const setAll = (elements, update) => elements.forEach(update);
      /**
       * @param {number} opacity
       * @param {number} scale
       */
      const glowAt = (opacity, scale) =>
        setAll(/** @type {HTMLElement[]} */ (this.glows), (element) => {
          element.style.opacity = String(opacity);
          element.style.transform = `translate(-50%, -50%) scale(${scale})`;
        });
      /** @type {{ opacity: number } | null} Glow opacity when its fade-out began */
      let glowFrom = null;
      /** @type {Tween[]} */
      const tweens = [
        [
          0,
          1.6,
          ease.power2InOut,
          (progress) => setAll(/** @type {HTMLElement[]} */ (this.counts), (element) => (element.textContent = String(Math.round(progress * 100)))),
        ],
        [0, 0.5, ease.sineOut, (progress) => setAll(/** @type {HTMLElement[]} */ (this.veilFluids), (element) => (element.style.opacity = String(progress)))],
        [
          0.25,
          1.55,
          ease.sineInOut,
          (progress) => setAll(/** @type {HTMLElement[]} */ (this.veils), (element) => (element.style.opacity = String(1 - progress))),
        ],
        [
          0,
          2.4,
          ease.power2Out,
          (progress) =>
            setAll([.../** @type {HTMLElement[]} */ (this.photos), .../** @type {HTMLElement[]} */ (this.scalers)], (element) => (element.style.transform = `scale(${1.06 - 0.06 * progress})`)),
        ],
        [
          0.15,
          0.9,
          ease.power1InOut,
          (progress) => setAll(/** @type {SVGElement[]} */ (this.outers), (element) => (element.style.strokeDashoffset = `${1020 * (1 - progress)}px`)),
        ],
        [
          1,
          0.2,
          ease.sineInOut,
          (progress) => setAll(/** @type {SVGElement[]} */ (this.bridges), (element) => (element.style.strokeDashoffset = `${1020 * (1 - progress)}px`)),
        ],
        [0.8, 1.2, ease.sineInOut, (progress) => (/** @type {HTMLElement} */ (this.feather).style.opacity = String(progress))],
        // Glow breathes until 0.8s, then fades out from wherever it is
        [
          0,
          0.8,
          ease.linear,
          (progress) => {
            const cycle = ((progress * 0.8) / 1.7) % 2;
            const breath = ease.sineInOut(cycle > 1 ? 2 - cycle : cycle);
            glowAt(0.45 + 0.45 * breath, 1 + 0.12 * breath);
          },
        ],
        [
          0.8,
          1,
          ease.sineOut,
          (progress) => {
            glowFrom ??= { opacity: parseFloat(/** @type {string} */ (/** @type {HTMLElement[]} */ (this.glows)[0]?.style.opacity)) || 0.45 };
            setAll(/** @type {HTMLElement[]} */ (this.glows), (element) => {
              element.style.opacity = String(/** @type {{ opacity: number }} */ (glowFrom).opacity * (1 - progress));
              if (progress === 1) element.style.visibility = 'hidden';
            });
          },
        ],
        [2.55, 0.5, ease.power2Out, (progress) => this.#fade(/** @type {HTMLElement[]} */ (this.loaders), progress)],
        [1.25, 0.9, ease.sineInOut, (progress) => this.#fade(/** @type {HTMLElement[]} */ (this.photos), progress)],
      ];
      /** @type {TimedCall[]} */
      const calls = [
        [
          2.1,
          () => {
            /** @type {Fluid} */ (this.veilFluid).destroy();
            /** @type {HTMLElement[]} */ (this.veils).forEach((veil) => veil.remove());
          },
        ],
        [2.3, () => this.#afterIntro()],
      ];
      const endTime = Math.max(...tweens.map(([start, duration]) => start + duration), ...calls.map(([at]) => at));

      // Touch devices: hold the page on the intro until it hands off (max 12s)
      /** @type {boolean} */
      this.touch = window.matchMedia('(hover: none), (pointer: coarse)').matches || 'ontouchstart' in window;
      /** @type {(event: Event) => void} */
      this.preventTouch = (event) => event.preventDefault();
      if (this.touch && this.#autoScrollEnabled()) {
        document.documentElement.classList.add('pro2-intro-lock');
        window.addEventListener('touchmove', this.preventTouch, { passive: false });
        /** @type {number} */
        this.unlockTimer = setTimeout(() => this.#unlock(), 12000);
      }
      // Desktop: if the visitor scrolls or clicks during the intro, wait before auto-scrolling
      /** @type {boolean} */
      this.interacted = false;
      /** @type {() => boolean} */
      this.onInteract = () => (this.interacted = true);
      /** @type {string[]} */
      this.interactEvents = this.touch ? [] : ['wheel', 'touchstart', 'pointerdown', 'keydown'];
      this.interactEvents.forEach((type) => window.addEventListener(type, /** @type {EventListener} */ (this.onInteract), { passive: true, once: true }));

      /** @type {number | null} */
      let startedAt = null;
      /** @param {number} now */
      const tick = (now) => {
        startedAt ??= now + 200;
        const elapsed = Math.max(0, (now - startedAt) / 1000);
        for (const [start, duration, easing, apply] of tweens) {
          if (elapsed >= start) apply(easing(clamp01((elapsed - start) / duration)));
        }
        while (calls.length && elapsed >= /** @type {TimedCall} */ (calls[0])[0]) /** @type {TimedCall} */ (calls.shift())[1]();
        if (elapsed < endTime || calls.length) this.timelineFrame = requestAnimationFrame(tick);
      };
      /** @type {number} */
      this.timelineFrame = requestAnimationFrame(tick);
    }

    /**
     * @param {HTMLElement[]} elements
     * @param {number} progress
     */
    #fade(elements, progress) {
      elements.forEach((element) => {
        element.style.opacity = String(1 - progress);
        element.style.visibility = progress === 1 ? 'hidden' : '';
      });
    }

    #autoScrollEnabled() {
      return this.dataset.autoScroll !== 'false' && !window.Shopify?.designMode;
    }

    #nextSection() {
      return this.closest('.shopify-section')?.nextElementSibling ?? null;
    }

    // Once the intro has played: move on to the next section when its images are ready
    #afterIntro() {
      /** @type {string[]} */ (this.interactEvents).forEach((type) => window.removeEventListener(type, /** @type {EventListener} */ (this.onInteract)));
      const next = this.#nextSection();
      if (!this.#autoScrollEnabled() || !next) return this.#unlock();

      // Reaching the next section by any means (auto-scroll or the visitor) retires the intro
      /** @type {() => void} */
      this.onScroll = () => {
        if (!this.scrolling && next.getBoundingClientRect().top <= 1) this.#retire(next);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      if (this.retired) return;

      const startY = window.scrollY;
      const stillHere = () => !this.retired && Math.abs(window.scrollY - startY) <= 2;
      // Lazy images below the fold never decode until scrolled to, so only wait on ones already loading
      const images = [this, next]
        .flatMap((section) => Array.from(section.querySelectorAll('img')))
        .filter((image) => image.loading !== 'lazy' || image.complete);
      const ready = Promise.race([
        Promise.all(images.map((image) => image.decode().catch(() => {}))),
        new Promise((resolve) => setTimeout(resolve, 6000)),
      ]);
      const scrollWhenReady = () => ready.then(() => stillHere() && this.#scrollTo(next));
      if (this.interacted) setTimeout(() => stillHere() && scrollWhenReady(), 3500);
      else scrollWhenReady();
    }

    /**
     * Eased scroll to the next section; any wheel/touch/key from the visitor cancels it
     * @param {Element} target
     */
    #scrollTo(target) {
      const fromY = window.scrollY;
      const toY = target.getBoundingClientRect().top + window.scrollY;
      const duration = 1800;
      const root = document.documentElement;
      const previousBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      this.#cancelAutoScroll();
      /** @type {boolean} */
      this.scrolling = true;
      /** @type {((event?: Event) => void) | null} Called with an event when the visitor interrupts */
      this.cancelScroll = (event) => {
        cancelAnimationFrame(/** @type {number} */ (this.scrollFrame));
        root.style.scrollBehavior = previousBehavior;
        ['wheel', 'keydown', 'pointerdown'].forEach((type) =>
          window.removeEventListener(type, /** @type {EventListener} */ (this.cancelScroll))
        );
        if (!this.touch) window.removeEventListener('touchstart', /** @type {EventListener} */ (this.cancelScroll));
        this.cancelScroll = null;
        this.scrolling = false;
        // Interrupted by the visitor: give them the page straight away
        if (event) this.#unlock();
      };
      ['wheel', 'keydown', 'pointerdown'].forEach((type) =>
        window.addEventListener(type, /** @type {EventListener} */ (this.cancelScroll), { passive: true })
      );
      if (!this.touch) window.addEventListener('touchstart', this.cancelScroll, { passive: true });

      /** @type {number | null} */
      let startTime = null;
      /** @param {number} now */
      const step = (now) => {
        startTime ??= now;
        const progress = clamp01((now - startTime) / duration);
        window.scrollTo(0, fromY + (toY - fromY) * ease.expoInOut(progress));
        if (progress < 1) this.scrollFrame = requestAnimationFrame(step);
        else this.#retire(target);
      };
      /** @type {number} */
      this.scrollFrame = requestAnimationFrame(step);
    }

    #cancelAutoScroll() {
      this.cancelScroll?.();
    }

    // Release the touch scroll lock
    #unlock() {
      clearTimeout(this.unlockTimer);
      document.documentElement.classList.remove('pro2-intro-lock');
      window.removeEventListener('touchmove', /** @type {EventListener} */ (this.preventTouch));
    }

    /**
     * The visitor has reached the next section: remove the intro for good, keeping the view where it is,
     * so scrolling back up starts at the next section like on the source page
     * @param {Element} next
     */
    #retire(next) {
      if (this.retired) return;
      /** @type {boolean} */
      this.retired = true;
      this.#cancelAutoScroll();
      this.#unlock();
      window.removeEventListener('scroll', /** @type {EventListener} */ (this.onScroll));

      const wrapper = this.closest('.shopify-section') ?? this;
      const shift = next.getBoundingClientRect().top - wrapper.getBoundingClientRect().top;
      const scrollY = window.scrollY;
      const root = document.documentElement;
      const previousBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      wrapper.remove();
      window.scrollTo(0, Math.max(0, scrollY - shift));
      root.style.scrollBehavior = previousBehavior;
      window.dispatchEvent(new CustomEvent('pro2-intro:retire'));
    }
  }

  customElements.define('pro2-intro', Pro2Intro);
}
